import { useAuth, useUser } from "@clerk/react"
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query"
import React from "react"
import { useLocation } from "react-router-dom"

import { getOrganizationAccessContext } from "@/api/get-organization-access-context"
import {
  OrganizationAccessContextV1,
  OrganizationAccessContextV1Schema,
} from "@/models/organization-access"
import { ClerkPublicMetadata } from "@/types/clerk-metadata"
import { isLearnWorldsRoute } from "@/lib/learnworlds"
import AccessState from "@/components/auth/AccessState"
import {
  accessRetryDelay,
  classifyAccessFailure,
  isFailedAccess,
  loadAccess,
} from "@/utils/access-failure"

interface OrganizationAccessValue {
  context: OrganizationAccessContextV1
  cacheKey: string
  queryKey: (...parts: readonly unknown[]) => readonly unknown[]
}

const OrganizationAccessContext = React.createContext<OrganizationAccessValue | null>(null)

export const organizationAccessQueryKey = (
  clerkUserId: string,
  vividUserId: string,
  organizationId: string,
) => ["organization-access-context", clerkUserId, vividUserId, organizationId] as const

export const createUserBoundQueryClient = (): QueryClient => new QueryClient()

const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#5D82ED] to-[#C08AF8]">
    <span className="text-sm font-medium text-white">Loading your access…</span>
  </div>
)

const unavailable = { kind: "unavailable", retryable: false } as const

function QueryClientBoundary({
  children,
  boundaryKey,
}: {
  children: React.ReactNode
  boundaryKey: string
}) {
  const queryClient = React.useMemo(createUserBoundQueryClient, [boundaryKey])
  React.useLayoutEffect(() => () => queryClient.clear(), [queryClient])
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}

export function IdentityQueryClientProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded: authLoaded, isSignedIn } = useAuth()
  const { isLoaded: userLoaded, user } = useUser()
  const boundaryKey =
    authLoaded && userLoaded
      ? isSignedIn && user
        ? `signed-in:${user.id}`
        : "signed-out"
      : "clerk-loading"

  return (
    <QueryClientBoundary key={boundaryKey} boundaryKey={boundaryKey}>
      {children}
    </QueryClientBoundary>
  )
}

const matchesRoute = (pathname: string, route: string): boolean =>
  pathname === route || pathname.startsWith(`${route}/`)

export const isPublicShareRoute = (pathname: string): boolean =>
  matchesRoute(pathname, "/public/form-structure-overview") ||
  matchesRoute(pathname, "/public/issues")

export const isSuperAdminRoute = (pathname: string): boolean =>
  matchesRoute(pathname, "/super-admin")

export const isAuthenticationRoute = (pathname: string): boolean =>
  matchesRoute(pathname, "/sign-in") || matchesRoute(pathname, "/sign-up")

export const routeBypassesOrganizationAccess = (pathname: string): boolean =>
  isLearnWorldsRoute(pathname) ||
  isPublicShareRoute(pathname) ||
  isSuperAdminRoute(pathname) ||
  isAuthenticationRoute(pathname) ||
  pathname === "/status"

function PrivateScope({
  children,
  clerkUserId,
  context,
}: {
  children: React.ReactNode
  clerkUserId: string
  context: OrganizationAccessContextV1
}) {
  const cacheKey = `${clerkUserId}:${context.userId}:${context.organizationId}:${context.scopeRevision}`
  const value: OrganizationAccessValue = {
    context,
    cacheKey,
    queryKey: (...parts) => [
      "scoped",
      clerkUserId,
      context.userId,
      context.organizationId,
      context.scopeRevision,
      ...parts,
    ],
  }

  return (
    <QueryClientBoundary boundaryKey={cacheKey}>
      <OrganizationAccessContext.Provider value={value}>
        {children}
      </OrganizationAccessContext.Provider>
    </QueryClientBoundary>
  )
}

function ProtectedOrganizationAccess({
  children,
  clerkUserId,
  vividUserId,
  organizationId,
}: {
  children: React.ReactNode
  clerkUserId: string
  vividUserId: string
  organizationId: string
}) {
  const queryClient = useQueryClient()
  const accessQueryKey = React.useMemo(
    () => organizationAccessQueryKey(clerkUserId, vividUserId, organizationId),
    [clerkUserId, vividUserId, organizationId],
  )
  React.useLayoutEffect(
    () => () => queryClient.removeQueries({ queryKey: accessQueryKey, exact: true }),
    [accessQueryKey, queryClient],
  )
  const accessQuery = useQuery({
    queryKey: accessQueryKey,
    queryFn: () =>
      loadAccess(
        async () => {
          const parsed = OrganizationAccessContextV1Schema.parse(
            await getOrganizationAccessContext.query<unknown>(),
          )
          if (parsed.userId !== vividUserId || parsed.organizationId !== organizationId)
            throw new Error("Access context did not match the current identity")
          return parsed
        },
        (failure) => {
          queryClient.setQueryData(accessQueryKey, failure)
        },
      ),
    retryDelay: accessRetryDelay,
    gcTime: 0,
    staleTime: 0,
    refetchInterval: 60_000,
    refetchOnMount: "always",
    refetchOnReconnect: "always",
    refetchOnWindowFocus: "always",
  })

  if (isFailedAccess(accessQuery.data))
    return (
      <AccessState
        failure={accessQuery.data.accessFailure}
        retry={() => void accessQuery.refetch()}
        pending={accessQuery.isFetching}
      />
    )
  if (!accessQuery.data) {
    if (accessQuery.isError || accessQuery.failureReason || accessQuery.isPaused)
      return (
        <AccessState
          failure={classifyAccessFailure(accessQuery.error ?? accessQuery.failureReason)}
          retry={() => void accessQuery.refetch()}
          pending={accessQuery.isFetching}
        />
      )
    return <LoadingScreen />
  }

  const cacheKey = `${clerkUserId}:${accessQuery.data.userId}:${organizationId}:${accessQuery.data.scopeRevision}`
  return (
    <PrivateScope key={cacheKey} clerkUserId={clerkUserId} context={accessQuery.data}>
      {children}
    </PrivateScope>
  )
}

function ProvisionOrganizationAccess({
  clerkUser,
  children,
}: {
  clerkUser: { id: string; reload: () => Promise<unknown> }
  children: React.ReactNode
}) {
  const queryClient = useQueryClient()
  const provisioningQueryKey = ["organization-access-provisioning", clerkUser.id] as const
  const provisioningQuery = useQuery({
    queryKey: provisioningQueryKey,
    queryFn: () =>
      loadAccess(
        async () => {
          const context = OrganizationAccessContextV1Schema.parse(
            await getOrganizationAccessContext.query<unknown>(),
          )
          await clerkUser.reload()
          return context
        },
        (failure) => {
          queryClient.setQueryData(provisioningQueryKey, failure)
        },
      ),
    retryDelay: accessRetryDelay,
    gcTime: 0,
  })

  if (isFailedAccess(provisioningQuery.data))
    return (
      <AccessState
        failure={provisioningQuery.data.accessFailure}
        retry={() => void provisioningQuery.refetch()}
        pending={provisioningQuery.isFetching}
      />
    )
  if (!provisioningQuery.data) {
    if (provisioningQuery.isError || provisioningQuery.failureReason || provisioningQuery.isPaused)
      return (
        <AccessState
          failure={classifyAccessFailure(
            provisioningQuery.error ?? provisioningQuery.failureReason,
          )}
          retry={() => void provisioningQuery.refetch()}
          pending={provisioningQuery.isFetching}
        />
      )
    return <LoadingScreen />
  }
  return (
    <ProtectedOrganizationAccess
      clerkUserId={clerkUser.id}
      vividUserId={provisioningQuery.data.userId}
      organizationId={provisioningQuery.data.organizationId}
    >
      {children}
    </ProtectedOrganizationAccess>
  )
}

export function OrganizationAccessProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded: authLoaded, isSignedIn } = useAuth()
  const { isLoaded: userLoaded, user } = useUser()
  const { pathname } = useLocation()

  if (!authLoaded || !userLoaded) return <LoadingScreen />
  if (routeBypassesOrganizationAccess(pathname) || !isSignedIn) return <>{children}</>

  const metadata = (user?.publicMetadata || {}) as ClerkPublicMetadata
  const organizationId = metadata.vivid?.organizationId
  const vividUserId = metadata.vivid?.id
  const clerkUserId = user?.id
  if (!user || !clerkUserId)
    return <AccessState failure={unavailable} retry={() => window.location.reload()} />
  if (!vividUserId || !organizationId)
    return <ProvisionOrganizationAccess clerkUser={user}>{children}</ProvisionOrganizationAccess>

  return (
    <ProtectedOrganizationAccess
      clerkUserId={clerkUserId}
      vividUserId={vividUserId}
      organizationId={organizationId}
    >
      {children}
    </ProtectedOrganizationAccess>
  )
}

export function useOrganizationAccess(): OrganizationAccessValue {
  const value = React.useContext(OrganizationAccessContext)
  if (!value) throw new Error("Organization access is unavailable")
  return value
}
